import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["screen", "pill", "progressBar", "footerTitle", "changeTypeBtn",
    "backBtn", "nextBtn", "nextBtnWrapper", "submitBtn", "confirmationScreen", "reportTypeCards",
    "reportTypeInput", "portfolioReportTypeInput", "submissionErrors", "structureCards",
    "reportStructureInput", "portfolioCountInput", "exactCountReveal", "totalCountInput",
    "scrollContainer"]
  static values = {
    current: { type: Number, default: 1 },
    ready: { type: Boolean, default: false },
    portfolioEnabled: { type: Boolean, default: false },
  }

  PROGRESS = [12, 28, 46, 62, 76, 89, 97]
  PROGRESS_PORTFOLIO = [11, 23, 36, 49, 62, 75, 88, 97]
  TOTAL = this.PROGRESS.length
  RE_ADDRESS_REPORT_TYPE = "re_address_of_vas_panel_report"
  RE_ADDRESS_WARNING_MODAL_ID = "nqr_re_address_warning_modal"
  RE_ADDRESS_NO_PERMISSION_MODAL_ID = "nqr_re_address_no_permission_modal"
  CHANGE_TYPE_CONFIRM_MODAL_ID = "nqr_change_type_confirm_modal"
  PORTFOLIO_REPORT_TYPE = "full_valuation_multi_property_portfolio"

  connect() {
    const n = parseInt(new URLSearchParams(window.location.search).get("screen"), 10)
    if (n >= 1 && n <= this.TOTAL) this.currentValue = n
    this.furthestValue = this.currentValue
    this._scheduleRecalc = this._scheduleRecalc.bind(this)
    this.element.addEventListener("change", this._scheduleRecalc)
    this.element.addEventListener("input", this._scheduleRecalc)
    this.element.addEventListener("trix-change", this._scheduleRecalc)
    this.#render()
    this.recalculateVisibility()
  }

  disconnect() {
    this.element.removeEventListener("change", this._scheduleRecalc)
    this.element.removeEventListener("input", this._scheduleRecalc)
    this.element.removeEventListener("trix-change", this._scheduleRecalc)
  }

  next() {
    if (this.currentValue >= this.#total()) return
    if (this.#needsReAddressConfirmation()) {
      this.#modal("open", this.RE_ADDRESS_WARNING_MODAL_ID)
      return
    }
    this.currentValue++
    this.#render()
  }

  confirmReAddress() {
    this.reAddressConfirmed = true
    this.#modal("close", this.RE_ADDRESS_WARNING_MODAL_ID)
    this.next()
  }

  declineReAddress() {
    this.#modal("close", this.RE_ADDRESS_WARNING_MODAL_ID)
    this.#modal("open", this.RE_ADDRESS_NO_PERMISSION_MODAL_ID)
  }

  restartAfterDecline() {
    this.#modal("close", this.RE_ADDRESS_NO_PERMISSION_MODAL_ID)
    this.#restartForm()
  }

  #needsReAddressConfirmation() {
    return this.currentValue === 1 &&
      !this.reAddressConfirmed &&
      this.#selectedReportType() === this.RE_ADDRESS_REPORT_TYPE
  }

  #selectedReportType() {
    return this.element.querySelector('input[name="quote_request[requested_report_type]"]')?.value
  }

  #modal(kind, id) {
    this.element.dispatchEvent(new CustomEvent(`local-modal-manager:${kind}`, { detail: { id } }))
  }

  prev() {
    if (this.currentValue > 1) {
      this.currentValue--
      this.#render()
    }
  }

  selectReportType(event) {
    const btn = event.currentTarget
    if (this.hasReportTypeCardsTarget) {
      this.reportTypeCardsTarget.querySelectorAll("button[data-report-type]").forEach(b => {
        delete b.dataset.selected
      })
    }
    btn.dataset.selected = "true"
    if (this.hasReportTypeInputTarget) {
      this.reportTypeInputTarget.value = btn.dataset.reportType
      this.reportTypeInputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    }
  }

  async submit() {
    if (!this.hasSubmitBtnTarget || this.submitBtnTarget.disabled) return
    this.submitBtnTarget.disabled = true
    this.#hideSubmissionErrors()
    try {
      const response = await fetch(this.element.action, {
        method: "POST",
        body: new FormData(this.element),
        headers: {
          Accept: "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "",
        },
        credentials: "same-origin",
      })
      const data = await response.json().catch(() => ({}))
      if (response.ok && data.redirect_url) {
        window.location.assign(data.redirect_url)
        return
      }
      this.#showSubmissionErrors(
        Array.isArray(data.errors) && data.errors.length ? data.errors : ["Something went wrong. Please try again."]
      )
    } catch {
      this.#showSubmissionErrors(["Something went wrong. Please try again."])
    }
    this.submitBtnTarget.disabled = false
  }

  #showSubmissionErrors(messages) {
    if (!this.hasSubmissionErrorsTarget) return
    this.submissionErrorsTarget.replaceChildren(...messages.map((message) => {
      const line = document.createElement("div")
      line.textContent = message
      return line
    }))
    this.submissionErrorsTarget.classList.remove("hidden")
    this.submissionErrorsTarget.scrollIntoView({ behavior: "smooth", block: "center" })
  }

  #hideSubmissionErrors() {
    if (!this.hasSubmissionErrorsTarget) return
    this.submissionErrorsTarget.classList.add("hidden")
    this.submissionErrorsTarget.replaceChildren()
  }

  goToScreen(event) {
    const n = parseInt(event.currentTarget.dataset.screen, 10)
    if (n >= 1 && n <= this.#total()) {
      this.currentValue = n
      this.#render()
    }
  }

  changeType() {
    this.#modal("open", this.CHANGE_TYPE_CONFIRM_MODAL_ID)
  }

  confirmChangeType() {
    this.#modal("close", this.CHANGE_TYPE_CONFIRM_MODAL_ID)
    this.#restartForm()
  }

  cancelChangeType() {
    this.#modal("close", this.CHANGE_TYPE_CONFIRM_MODAL_ID)
  }

  #restartForm() {
    // Drop furthestValue before resetSelection: its selectionChanged event is dispatched
    // synchronously and must not re-enter #resetAfterTypeChange mid-reset.
    this.furthestValue = 1
    const typeSelector = document.querySelector('[data-controller~="nqr-type-selector"]')
    if (typeSelector) {
      this.application
          .getControllerForElementAndIdentifier(typeSelector, "nqr-type-selector")
          ?.resetSelection?.()
    }
    this.#clearFieldsIn(this.element)
    this.#clearReportType()
    this.#clearPortfolioInputs()
    this.#collapseBulkUploadManualFields()
    this.reAddressConfirmed = false
    this.currentValue = 1
    this.#render()
    this.recalculateVisibility()
  }

  // Changing the valuation type starts the form over, so wipe the report-type selection —
  // both the hidden input (a hidden field #clearInputsIn intentionally leaves alone) and the
  // highlighted card.
  #clearReportType() {
    if (this.hasReportTypeCardsTarget) {
      this.reportTypeCardsTarget.querySelectorAll("button[data-report-type]").forEach((b) => delete b.dataset.selected)
    }
    if (this.hasReportTypeInputTarget) this.reportTypeInputTarget.value = ""
  }

  typeSelectionComplete(event) {
    this.readyValue = event.detail.complete
    if (!this.#portfolioSelected()) this.#clearPortfolioInputs()
    if (this.furthestValue > 1) {
      this.#resetAfterTypeChange()
      return
    }
    this.recalculateVisibility()
  }

  selectStructure(event) {
    const btn = event.currentTarget
    if (this.hasStructureCardsTarget) {
      this.structureCardsTarget.querySelectorAll("button[data-structure-id]").forEach(b => {
        delete b.dataset.selected
      })
    }
    btn.dataset.selected = "true"
    if (this.hasReportStructureInputTarget) {
      this.reportStructureInputTarget.value = btn.dataset.structureId
      this.reportStructureInputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    }
    this.#applyStructureToCountOptions(btn.dataset.structureId)
    this.#portfolioSelectionChanged()
  }

  countOptionChanged(event) {
    if (event.target.value === "6_or_more") {
      this.#toggleExactReveal(true)
      this.#syncExactCount()
    } else {
      this.#toggleExactReveal(false)
      this.#clearExactInput()
      this.#setCount(event.target.value)
    }
    this.#portfolioSelectionChanged()
  }

  exactCountChanged() {
    this.#syncExactCount()
    this.#portfolioSelectionChanged()
  }

  // The Step 3 count mirrors the Step 2 exact count: one canonical value in
  // quote_request[portfolio_property_count]. An amendment below 6 is not synced —
  // it would dissolve the very step the user is standing on — the data-nqr-min
  // engine flags it and gates Continue instead.
  totalCountChanged() {
    if (!this.hasTotalCountInputTarget) return
    const value = parseInt(this.totalCountInputTarget.value, 10)
    if (value >= 6) {
      this.#setCount(String(value))
      const exact = this.#exactCountInput()
      if (exact) exact.value = String(value)
    }
    this.#updateNextBtn()
  }

  #portfolioSelectionChanged() {
    this.#syncExcludedScreens()
    this.#updatePills()
    this.#updateProgress()
    this.#updateNextBtn()
  }

  // Any touch of the valuation-type cascade after later steps were reached invalidates
  // every answer (all downstream fields are conditional on the type), so the form resets
  // while the selector keeps the user's new selection.
  #resetAfterTypeChange() {
    this.screenTargets.slice(1).forEach((screen) => this.#clearFieldsIn(screen))
    this.#clearReportType()
    this.#clearPortfolioInputs()
    this.#collapseBulkUploadManualFields()
    this.reAddressConfirmed = false
    this.furthestValue = 1
    this.#render()
    this.recalculateVisibility()
  }

  #collapseBulkUploadManualFields() {
    const screen = this.screenTargets.find(el => el.dataset.nqrScreen === "bulk_upload")
    screen?.querySelector('[data-nqr-address-lookup-target="manualFields"]')?.classList?.add("hidden")
  }

  #clearFieldsIn(scope) {
    this.#clearControllerSelection(scope, "single-select-field")
    this.#clearControllerSelection(scope, "single-select-field-component-with-search")
    this.#clearControllerSelection(scope, "multi-select-chips-with-search")
    this.#clearControllerSelection(scope, "file-upload")
    scope.querySelectorAll("trix-editor").forEach((trix) => trix.editor?.loadHTML(""))
    this.#clearInputsIn(scope)
  }

  recalculateVisibility() {
    let changed = true
    let iterations = 0
    while (changed && iterations < 10) {
      changed = false
      iterations++
      const state = this.#collectFormState()
      this.element.querySelectorAll("[data-nqr-visibility-rule]").forEach((el) => {
        const visible = this.#evaluateRules(this.#parseRules(el.dataset.nqrVisibilityRule), state)
        const wasHidden = el.classList.contains("hidden")
        el.classList.toggle("hidden", !visible)
        if (!visible && !wasHidden) {
          this.#clearInputsIn(el)
          changed = true
        }
      })
      this.element.querySelectorAll("[data-nqr-hide-when]").forEach((el) => {
        const shouldHide = this.#evaluateRules(this.#parseRules(el.dataset.nqrHideWhen), state)
        const wasHidden = el.classList.contains("hidden")
        el.classList.toggle("hidden", shouldHide)
        if (shouldHide && !wasHidden) {
          this.#clearInputsIn(el)
          changed = true
        }
      })
    }
    this.#updatePlaceholders()
    this.#updateFieldErrors()
    this.#syncExcludedScreens()
    this.#syncPortfolioReportType()
    this.#updateNextBtn()
    this.#updateSubmitBtn()
    this.#updatePills()
  }

  // The portfolio report format is not chosen by the user — it is derived from the
  // report_structure picked in Step 2. Reveal the matching banner/card and mirror its
  // report-type into the hidden input so submission persists the right value.
  #syncPortfolioReportType() {
    const screen = this.screenTargets.find(el => el.dataset.nqrScreen === "portfolio-report-type")
    if (!screen) return
    const structure = this.hasReportStructureInputTarget ? this.reportStructureInputTarget.value : ""
    screen.querySelectorAll("[data-structure-variant]").forEach(el => {
      el.classList.toggle("hidden", el.dataset.structureVariant !== structure)
    })
    const activeCard = screen.querySelector(
      `[data-structure-variant="${structure}"] button[data-report-type]`
    )
    if (this.hasPortfolioReportTypeInputTarget) {
      this.portfolioReportTypeInputTarget.value = activeCard ? activeCard.dataset.reportType : ""
    }
  }

  #updatePlaceholders() {
    const state = this.#collectFormState()
    this.element.querySelectorAll("[data-nqr-placeholder-variants]").forEach((el) => {
      const target = el.querySelector("trix-editor")
        || el.querySelector('input:not([type="hidden"]):not([type="url"]), textarea')
      if (!target) return
      const variants = this.#parseRules(el.dataset.nqrPlaceholderVariants)
      const matched = variants.find((variant) => this.#evaluateRules(variant.rules || [], state))
      target.setAttribute("placeholder", matched?.placeholder || el.dataset.nqrPlaceholderDefault || "")
    })
  }

  #clearInputsIn(scope) {
    scope.querySelectorAll("input, textarea, select").forEach((el) => {
      if (el.type === "hidden") return
      if (el.type === "radio" || el.type === "checkbox") {
        el.checked = false
      } else if (el.tagName === "SELECT") {
        el.selectedIndex = 0
      } else {
        el.value = ""
      }
    })
    scope.querySelectorAll("trix-editor").forEach((trix) => trix.editor?.loadHTML(""))
  }

  #clearControllerSelection(scope, identifier) {
    scope.querySelectorAll(`[data-controller~="${identifier}"]`).forEach((el) => {
      this.application.getControllerForElementAndIdentifier(el, identifier)?.clearSelection?.()
    })
  }

  _scheduleRecalc() {
    if (this._recalcQueued) return
    this._recalcQueued = true
    requestAnimationFrame(() => {
      this._recalcQueued = false
      this.recalculateVisibility()
    })
  }

  #parseRules(raw) {
    try {
      const parsed = JSON.parse(raw)
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }

  #evaluateRules(rules, state) {
    if (rules.length === 0) return false
    return rules.some((rule) => this.#evaluateRule(rule, state))
  }

  #evaluateRule(rule, state) {
    return Object.entries(rule).every(([key, expected]) => this.#matches(state[key], expected))
  }

  #matches(actual, expected) {
    const actualStr = actual === undefined || actual === null ? "" : String(actual)
    if (Array.isArray(expected)) return expected.map(e => String(e)).includes(actualStr)
    return actualStr === String(expected)
  }

  #collectFormState() {
    const state = {}
    this.element.querySelectorAll("[data-nqr-field-key]").forEach((el) => {
      // Excluded twin screens carry the same field keys as their active counterparts;
      // their (stale or empty) values must not clobber the active screen's state.
      if (el.closest('[data-nqr-excluded="true"]')) return
      const key = el.dataset.nqrFieldKey
      const value = this.#readFieldValue(el)
      if (value !== undefined && value !== "") state[key] = value
    })
    return state
  }

  #readFieldValue(el) {
    if (el.tagName === "INPUT") {
      if (el.type === "radio") return el.checked ? el.value : undefined
      return el.value
    }
    if (el.tagName === "SELECT" || el.tagName === "TEXTAREA") return el.value

    if (el.querySelector("trix-editor")) {
      return this.#trixTextOf(el.querySelector('input[type="hidden"]')?.value)
    }

    if (el.querySelector('input[type="radio"]')) {
      return el.querySelector('input[type="radio"]:checked')?.value
    }

    const hidden = el.querySelector('input[type="hidden"]')
    if (hidden) return hidden.value

    return el.querySelector("input, select, textarea")?.value
  }

  #trixTextOf(html) {
    if (!html) return ""
    if (/<figure|<img/i.test(html)) return "attachment"
    return html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim()
  }

  #allRequiredFilled() {
    return this.#screenFilled(this.currentValue - 1)
  }

  // index is a position in #activeScreens(), so step numbers stay stable whether or not
  // the Portfolio screen is inserted.
  #screenFilled(index) {
    if (index === 0) return this.readyValue
    const screen = this.#activeScreens()[index]
    if (!screen) return false
    return this.#unfilledRequiredLabelsFor(screen).length === 0
  }

  #unfilledRequiredLabelsFor(screen) {
    if (!screen) return []
    const single = Array.from(screen.querySelectorAll('[data-nqr-required="true"]'))
      .filter((el) => !el.classList.contains("hidden"))
      .filter((el) => this.#isFieldEmpty(el))
      .map((el) => el.dataset.nqrRequiredLabel || el.getAttribute("name") || "")
      .filter((label) => label !== "")
    return [...single, ...this.#unfilledRequiredGroupsFor(screen), ...this.#invalidValueLabelsFor(screen)]
  }

  EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/
  VALIDATED_FIELDS_SELECTOR = "[data-nqr-validate-email], [data-nqr-max], [data-nqr-min], [data-nqr-min-length]"

  // Mirrors the server-side format/range validations (email format, numericality caps)
  // so a value the server would reject blocks Continue with the same tooltip mechanism.
  #invalidValueLabelsFor(screen) {
    return Array.from(screen.querySelectorAll(this.VALIDATED_FIELDS_SELECTOR))
      .filter((el) => !el.classList.contains("hidden"))
      .map((el) => {
        const message = this.#invalidValueMessage(el)
        return message && `${this.#fieldReviewLabel(el)} (${message})`
      })
      .filter(Boolean)
  }

  #invalidValueMessage(el) {
    const value = String(this.#readFieldValue(el) ?? "").trim()
    if (value === "") return null
    if (el.dataset.nqrValidateEmail && !this.EMAIL_PATTERN.test(value)) return "invalid email address"
    if (el.dataset.nqrMax && parseFloat(value.replace(/,/g, "")) > parseFloat(el.dataset.nqrMax)) {
      return `maximum ${Number(el.dataset.nqrMax).toLocaleString("en-GB")}`
    }
    if (el.dataset.nqrMin && parseFloat(value.replace(/,/g, "")) < parseFloat(el.dataset.nqrMin)) {
      return `minimum ${Number(el.dataset.nqrMin).toLocaleString("en-GB")}`
    }
    if (el.dataset.nqrMinLength && value.length < parseInt(el.dataset.nqrMinLength, 10)) {
      return `must contain at least ${el.dataset.nqrMinLength} characters`
    }
    return null
  }

  // Inline field feedback (red border + message under the input) the moment an invalid
  // value is entered — matching the legacy form — not only at the Continue gate.
  #updateFieldErrors() {
    this.element.querySelectorAll(this.VALIDATED_FIELDS_SELECTOR).forEach((el) => {
      this.#toggleFieldError(el, this.#invalidValueMessage(el))
    })
  }

  #toggleFieldError(el, message) {
    const input = el.querySelector('input:not([type="hidden"]), textarea, trix-editor')
    let error = el.querySelector("[data-nqr-field-error]")
    input?.classList?.toggle("border-red-300", !!message)
    if (!message) {
      error?.remove()
      return
    }
    if (!error) {
      error = document.createElement("p")
      error.dataset.nqrFieldError = ""
      error.className = "mt-8 text-paragraph-s text-red-1000"
      el.appendChild(error)
    }
    error.textContent = message.charAt(0).toUpperCase() + message.slice(1)
  }

  // A required-group is satisfied when ANY of its members has a value (e.g. applicant
  // name OR company name). It contributes its label once when every member is empty.
  #unfilledRequiredGroupsFor(screen) {
    const groups = new Map()
    screen.querySelectorAll("[data-nqr-required-group]").forEach((el) => {
      if (el.classList.contains("hidden")) return
      const key = el.dataset.nqrRequiredGroup
      const group = groups.get(key) || { label: el.dataset.nqrRequiredGroupLabel || key, filled: false }
      if (!this.#isFieldEmpty(el)) group.filled = true
      groups.set(key, group)
    })
    return Array.from(groups.values()).filter((g) => !g.filled).map((g) => g.label)
  }

  #isFieldEmpty(el) {
    const value = this.#readFieldValue(el)
    return value === undefined || String(value).trim() === ""
  }

  #render() {
    if (this.currentValue > this.furthestValue) this.furthestValue = this.currentValue
    this.#syncExcludedScreens()
    this.#prefillTotalCount()
    if (this.currentValue === this.#total()) this.#populateReview()
    this.#showScreen()
    this.#updateProgress()
    this.#updatePills()
    this.#updateFooter()
    this.#updateNextBtn()
    this.#updateSubmitBtn()
  }

  #showScreen() {
    const actives = this.#activeScreens()
    this.screenTargets.forEach(el => {
      const idx = actives.indexOf(el)
      el.classList.toggle("hidden", idx === -1 || idx + 1 !== this.currentValue)
    })
    if (this.hasScrollContainerTarget) this.scrollContainerTarget.scrollTop = 0
  }

  #updateProgress() {
    const pct = this.#progressValues()[this.currentValue - 1] || 0
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${pct}%`
    }
  }

  // Without the Report Details step the Review step moves up a slot, so it takes
  // over the dropped second-to-last progress entry's position.
  #progressValues() {
    if (this.#portfolioActive()) return this.PROGRESS_PORTFOLIO
    if (this.#reportFormatAvailable()) return this.PROGRESS
    return [...this.PROGRESS.slice(0, -2), this.PROGRESS.at(-1)]
  }

  #updatePills() {
    const actives = this.#activePills()
    this.pillTargets.forEach(pill => {
      const idx = actives.indexOf(pill)
      pill.classList.toggle("hidden", idx === -1)
      if (idx === -1) return

      this.#applyPillLabel(pill)
      const n = idx + 1
      const num = pill.querySelector(".nqr-pill-num")
      if (num) num.textContent = n
      pill.classList.remove("nqr-pill-active", "nqr-pill-done", "nqr-pill-inactive")
      if (n === this.currentValue) {
        pill.classList.add("nqr-pill-active")
        setTimeout(() => this.#scrollPillIntoView(pill), 50)
      } else if (n <= this.furthestValue && this.#screenFilled(idx)) {
        // Only a step the user has actually reached (and completed) shows the done tick — a
        // not-yet-visited step must not appear done even when it has no required fields.
        pill.classList.add("nqr-pill-done")
      } else {
        pill.classList.add("nqr-pill-inactive")
      }
    })
  }

  #applyPillLabel(pill) {
    if (!pill.dataset.nqrLabelDefault) return
    const label = pill.querySelector(".nqr-pill-label")
    if (!label) return
    label.textContent = this.#portfolioPropertiesActive()
      ? pill.dataset.nqrLabelPortfolio
      : pill.dataset.nqrLabelDefault
  }

  #scrollPillIntoView(pill) {
    const nav = pill.closest(".overflow-x-auto")
    if (!nav) return
    const navRect = nav.getBoundingClientRect()
    const pillRect = pill.getBoundingClientRect()
    const target = nav.scrollLeft + pillRect.left - navRect.left - (navRect.width - pillRect.width) / 2
    nav.scrollTo({ left: Math.max(0, target), behavior: "smooth" })
  }

  #updateFooter() {
    const n = this.currentValue
    if (this.hasBackBtnTarget) this.backBtnTarget.classList.toggle("hidden", n === 1)
    if (this.hasChangeTypeBtnTarget) this.changeTypeBtnTarget.classList.toggle("hidden", n < 2)
    if (this.hasFooterTitleTarget) {
      this.footerTitleTarget.textContent = n === 1 ? "New quote request" : this.#valTypeLabel()
    }
    const isLast = n === this.#total()
    if (this.hasNextBtnTarget) this.nextBtnTarget.classList.toggle("hidden", isLast)
    if (this.hasSubmitBtnTarget) this.submitBtnTarget.classList.toggle("hidden", !isLast)
    if (this.hasNextBtnTarget) {
      this.nextBtnTarget.textContent = n === this.#total() - 1 ? "Review & Submit →" : "Continue →"
    }
  }

  #updateNextBtn() {
    if (!this.hasNextBtnTarget) return
    if (this.currentValue === 1) {
      this.nextBtnTarget.disabled = !this.readyValue
      this.#setNextBtnTooltip(null)
      return
    }
    const missing = this.#unfilledRequiredLabelsFor(this.#activeScreens()[this.currentValue - 1])
    this.nextBtnTarget.disabled = missing.length > 0
    this.#setNextBtnTooltip(missing.length > 0 ? this.#missingFieldsMessage(missing) : null)
  }

  #updateSubmitBtn() {
    if (!this.hasSubmitBtnTarget) return
    this.submitBtnTarget.disabled = this.#activeScreens().some((_, i) => !this.#screenFilled(i))
  }

  #missingFieldsMessage(missing) {
    return ["Required fields still to fill:", ...missing.map((label) => `– ${label}`)].join("\n")
  }

  #setNextBtnTooltip(text) {
    if (!this.hasNextBtnWrapperTarget) return
    const host = this.nextBtnWrapperTarget
    if (text) {
      host.dataset.inlineTooltipMessageValue = text
      host.dataset.inlineTooltipPlacementValue = "top"
      if (!(host.getAttribute("data-controller") || "").includes("inline-tooltip")) {
        host.setAttribute("data-controller", "inline-tooltip")
      }
    } else {
      host.removeAttribute("data-controller")
      delete host.dataset.inlineTooltipMessageValue
      delete host.dataset.inlineTooltipPlacementValue
    }
  }

  #valTypeLabel() {
    return document.getElementById("nqr-val-type-label")?.textContent || "New quote request"
  }

  #total() {
    return this.#activeScreens().length
  }

  #activeScreens() {
    return this.screenTargets.filter(el => this.#screenActive(el.dataset.nqrScreen))
  }

  // The Properties screen replaces Property Details in place, but the two share a single
  // pill whose label swaps between them, so the screen check diverges from the pill check.
  // The two report-type screens swap the same way, both behind the single Report Type pill.
  #screenActive(screen) {
    if (screen === "portfolio-properties") return this.#portfolioPropertiesActive()
    if (screen === "property-details") return this.#stepActive("property_details") && !this.#portfolioPropertiesActive()
    if (screen === "report-type") return !this.#portfolioActive() && this.#reportFormatAvailable()
    if (screen === "portfolio-report-type") return this.#portfolioActive()
    return this.#stepActive(screen)
  }

  #activePills() {
    return this.pillTargets.filter(el => this.#stepActive(el.dataset.nqrStep))
  }

  #stepActive(step) {
    if (step === "portfolio") return this.#portfolioActive()
    if (step === "bulk_upload") return this.#bulkUploadActive()
    if (step === "property_details") return !this.#bulkUploadActive()
    if (step === "report_type") return this.#portfolioActive() || this.#reportFormatAvailable()
    return true
  }

  // Mirrors the legacy form, where the report format field only exists when some
  // QuoteReportType schema matches the selected valuation type — re-address and
  // re-inspection have none, so the whole Report Details step drops out.
  #reportFormatAvailable() {
    if (!this.readyValue || !this.hasReportTypeCardsTarget) return true
    const state = this.#collectFormState()
    return Array.from(this.reportTypeCardsTarget.querySelectorAll("[data-nqr-visibility-rule]"))
      .some((el) => this.#evaluateRules(this.#parseRules(el.dataset.nqrVisibilityRule), state))
  }

  #portfolioActive() {
    return this.portfolioEnabledValue && this.#portfolioSelected()
  }

  // The Properties step replaces the single Property Details step only for explicit
  // 2-5 counts; "6 or more" goes through bulk upload instead.
  #portfolioPropertiesActive() {
    if (!this.#portfolioActive()) return false
    const count = this.#portfolioCount()
    return count >= 2 && count <= 5
  }

  #portfolioCount() {
    if (!this.hasPortfolioCountInputTarget) return NaN
    return parseInt(this.portfolioCountInputTarget.value, 10)
  }

  #portfolioSelected() {
    return this.#selectedReportType() === this.PORTFOLIO_REPORT_TYPE
  }

  #bulkUploadActive() {
    return this.#portfolioActive() &&
      this.#selectedStructure() === "portfolio_single_report" &&
      this.#selectedCount() >= 6
  }

  #selectedStructure() {
    return this.hasReportStructureInputTarget ? this.reportStructureInputTarget.value : ""
  }

  #selectedCount() {
    if (!this.hasPortfolioCountInputTarget) return 0
    return parseInt(this.portfolioCountInputTarget.value, 10) || 0
  }

  // Screens excluded from the flow (the Portfolio step outside portfolio mode, the Bulk
  // Upload / Property Details twin that lost the swap) hold inputs with the same names and
  // ids as their active counterparts. Disabling keeps them out of the submitted params and
  // the form state; dropping their ids keeps label[for] clicks on the active screen's inputs.
  #syncExcludedScreens() {
    const actives = this.#activeScreens()
    this.screenTargets.forEach(screen => {
      const excluded = !actives.includes(screen)
      if (excluded === (screen.dataset.nqrExcluded === "true")) return
      screen.dataset.nqrExcluded = excluded ? "true" : "false"
      screen.querySelectorAll("input, textarea, select, button").forEach(el => {
        // Trix reads its input's disabled state once, at editor initialisation, and resolves
        // the input by id — disabling or re-iding a trix input bricks the editor. Parking the
        // name keeps the value out of the submitted params without Trix noticing anything.
        if (el.dataset.trixInputTarget === "input") {
          if (excluded && el.getAttribute("name")) {
            el.dataset.nqrOriginalName = el.getAttribute("name")
            el.removeAttribute("name")
          } else if (!excluded && el.dataset.nqrOriginalName) {
            el.setAttribute("name", el.dataset.nqrOriginalName)
            delete el.dataset.nqrOriginalName
          }
          return
        }
        if (excluded) {
          if (el.disabled) el.dataset.nqrWasDisabled = "true"
          el.disabled = true
          if (el.id) {
            el.dataset.nqrOriginalId = el.id
            el.removeAttribute("id")
          }
        } else {
          if (el.dataset.nqrWasDisabled !== "true") el.disabled = false
          delete el.dataset.nqrWasDisabled
          if (el.dataset.nqrOriginalId) {
            el.id = el.dataset.nqrOriginalId
            delete el.dataset.nqrOriginalId
          }
        }
      })
    })
  }

  #prefillTotalCount() {
    if (!this.#bulkUploadActive() || !this.hasTotalCountInputTarget) return
    if (this.totalCountInputTarget.value !== "") return
    const count = this.#selectedCount()
    if (count >= 6) this.totalCountInputTarget.value = String(count)
  }

  #applyStructureToCountOptions(structureId) {
    const separate = structureId === "portfolio_separate_report"
    this.#countRadios().forEach(radio => {
      const singleOnly = radio.dataset.singleReportOnly === "true"
      const wrapper = radio.closest("label")
      if (wrapper) wrapper.classList.toggle("hidden", separate && singleOnly)
      if (separate && singleOnly && radio.checked) {
        radio.checked = false
        this.#setCount("")
        this.#toggleExactReveal(false)
        this.#clearExactInput()
      }
    })
  }

  #countRadios() {
    return this.element.querySelectorAll('input[type="radio"][name="portfolio_property_count_option"]')
  }

  // The hidden count input is written programmatically, so no native change event fires —
  // the Properties step listens for this custom event to keep its card list in sync.
  #setCount(value) {
    if (this.hasPortfolioCountInputTarget) this.portfolioCountInputTarget.value = value
    this.element.dispatchEvent(
      new CustomEvent("nqr-form:portfolioCountChanged", { bubbles: true, detail: { count: value } })
    )
  }

  #toggleExactReveal(show) {
    if (!this.hasExactCountRevealTarget) return
    this.#toggleRevealClasses(this.exactCountRevealTarget, show)
  }

  #toggleRevealClasses(el, show) {
    if (show) {
      el.classList.remove("max-h-0", "opacity-0", "overflow-hidden")
      el.classList.add("max-h-[1200px]", "opacity-100")
    } else {
      el.classList.add("max-h-0", "opacity-0", "overflow-hidden")
      el.classList.remove("max-h-[1200px]", "opacity-100")
    }
  }

  #exactCountInput() {
    if (!this.hasExactCountRevealTarget) return null
    return this.exactCountRevealTarget.querySelector('input[name="portfolio_exact_property_count"]')
  }

  #clearExactInput() {
    const input = this.#exactCountInput()
    if (input) input.value = ""
    this.#setExactCountError(false)
  }

  #syncExactCount() {
    const input = this.#exactCountInput()
    if (!input) return
    const value = parseInt(input.value, 10)
    const valid = value >= 6
    this.#setCount(valid ? String(value) : "")
    this.#setExactCountError(input.value !== "" && !valid)
    if (valid && this.hasTotalCountInputTarget) this.totalCountInputTarget.value = String(value)
  }

  #setExactCountError(show) {
    if (!this.hasExactCountRevealTarget) return
    const helper = this.exactCountRevealTarget.querySelector('[data-input-target="helper"]')
    if (!helper) return
    if (this.exactCountHelperDefault === undefined) this.exactCountHelperDefault = helper.textContent
    helper.textContent = show ? "Please enter a number of 6 or more." : this.exactCountHelperDefault
    helper.classList.toggle("text-red-1000", show)
  }

  #clearPortfolioInputs() {
    const screen = this.screenTargets.find(el => el.dataset.nqrScreen === "portfolio")
    if (!screen) return

    screen.querySelectorAll("input, textarea").forEach(input => {
      if (input.type === "radio" || input.type === "checkbox") input.checked = false
      else input.value = ""
    })
    screen.querySelectorAll("button[data-structure-id]").forEach(b => delete b.dataset.selected)
    this.#toggleExactReveal(false)
    this.#setExactCountError(false)
    this.#applyStructureToCountOptions("")
    this.element.dispatchEvent(new CustomEvent("nqr-form:portfolioReset", { bubbles: true }))
  }

  // The review screen re-reads the live form DOM on every entry, so what the user
  // sees is always exactly what the form would submit. Blank fields produce no row.
  // Each review card is resolved to its live screen by key, so the cards shown and their
  // Edit targets track #activeScreens() and stay correct as portfolio screens come and go.
  #populateReview() {
    const template = this.element.querySelector("[data-nqr-review-row-template]")
    if (!template) return
    const active = this.#activeScreens()
    this.element.querySelectorAll("[data-nqr-review-card]").forEach((card) => {
      const key = card.dataset.nqrReviewCard
      const screen = this.screenTargets.find((el) => el.dataset.nqrScreen === key)
      const position = screen ? active.indexOf(screen) : -1
      card.classList.toggle("hidden", position === -1)
      if (position === -1) return
      const editBtn = card.querySelector("[data-nqr-review-edit]")
      if (editBtn) editBtn.dataset.screen = position + 1
      const rows = key === "valuation-type" ? this.#valuationTypeRows() : this.#screenReviewRows(screen)
      card.querySelector("[data-nqr-review-section]").replaceChildren(
        ...rows.map((row) => this.#reviewRowFrom(template, row))
      )
    })
  }

  #reviewRowFrom(template, { label, labelHtml, value }) {
    const row = template.content.firstElementChild.cloneNode(true)
    const labelEl = row.querySelector("[data-nqr-review-label]")
    if (labelHtml) labelEl.innerHTML = labelHtml
    else labelEl.textContent = label
    const valueEl = row.querySelector("[data-nqr-review-value]")
    if (value instanceof HTMLElement) {
      valueEl.replaceChildren(value)
    } else if (value && value.html) {
      // Rich-text answers keep their formatting — the summary must show exactly what
      // will be submitted. The HTML is the user's own local trix state, not server data.
      valueEl.innerHTML = value.html
      valueEl.classList.add("trix-content")
    } else {
      valueEl.textContent = value
    }
    return row
  }

  #valuationTypeRows() {
    const labels = ["Report type", "Sector", "Property type"]
    const text = document.getElementById("nqr-val-type-label")?.textContent?.trim() || ""
    if (!text || text === "New quote request") return []
    return text.split(" · ")
      .map((value, i) => ({ label: labels[i], value: value.trim() }))
      .filter(({ label, value }) => label && value)
  }

  #screenReviewRows(screen) {
    if (!screen) return []
    switch (screen.dataset.nqrScreen) {
      case "portfolio": return this.#portfolioRows(screen)
      case "bulk_upload": return this.#bulkUploadRows(screen)
      case "portfolio-properties": return this.#portfolioPropertiesRows(screen)
      case "portfolio-report-type": return this.#portfolioReportTypeRows(screen)
      default:
        return [
          ...this.#addressReviewRows(screen),
          ...this.#fieldReviewRows(screen),
          ...this.#attachmentsReviewRows(screen),
          ...this.#contactsReviewRows(screen),
        ]
    }
  }

  #portfolioRows(screen) {
    return [
      this.#structureRow(screen),
      this.#countRow(screen),
      this.#portfolioFieldRow(screen, "estimated_value"),
      this.#portfolioFieldRow(screen, "annual_rent"),
      this.#portfolioFieldRow(screen, "portfolio_description"),
    ].filter(Boolean)
  }

  #structureRow(screen) {
    const input = screen.querySelector('[data-nqr-form-target="reportStructureInput"]')
    const value = input?.value
    if (!value) return null
    const card = screen.querySelector(`button[data-structure-id="${value}"]`)
    if (!card?.dataset.structureLabel) return null
    return { label: this.#portfolioLabel(input.closest("[data-nqr-review-field]")), value: card.dataset.structureLabel }
  }

  #countRow(screen) {
    const input = screen.querySelector('[data-nqr-form-target="portfolioCountInput"]')
    const count = parseInt(input?.value, 10)
    if (!(count >= 1)) return null
    const value = `${count} ${count === 1 ? "property" : "properties"}`
    return { label: this.#portfolioLabel(input.closest("[data-nqr-review-field]")), value }
  }

  #portfolioFieldRow(screen, name) {
    const wrapper = screen.querySelector(`[data-nqr-review-field="${name}"]`)
    if (!wrapper) return null
    const value = this.#inputReviewValue(wrapper)
    return value ? { label: this.#portfolioLabel(wrapper), value } : null
  }

  #portfolioLabel(wrapper) {
    return (wrapper?.dataset.nqrRequiredLabel || wrapper?.dataset.nqrReviewLabel || "").trim()
  }

  #portfolioPropertiesRows(screen) {
    const cards = Array.from(screen.querySelectorAll('[data-nqr-portfolio-properties-target="card"]'))
      .filter((card) => !card.classList.contains("hidden"))
    return cards.map((card, index) => {
      const address = this.#cardAddress(card)
      if (!address) return null
      const label = `Property ${index + 1}`
      return card.dataset.nqrCardPrimary === "true"
        ? { labelHtml: `${label} ${this.#primaryBadge()}`, value: address }
        : { label, value: address }
    }).filter(Boolean)
  }

  #cardAddress(card) {
    const part = (t) => card.querySelector(`[data-nqr-address-lookup-target="${t}"]`)?.value?.trim()
    return ["line1", "line2", "line3", "line4", "city", "county", "postcode"].map(part).filter(Boolean).join(", ")
  }

  // Reuses the exact pill class list from property_card_component.html.erb:19 (already in the
  // built CSS). The string is controller-authored with no user data, so innerHTML is safe.
  #primaryBadge() {
    return '<span class="text-paragraph-xxs font-bold uppercase tracking-widest text-brand-1000 ' +
      'bg-brand-100 rounded-full px-8 py-2">Primary</span>'
  }

  #portfolioReportTypeRows(screen) {
    const rows = []
    const card = screen.querySelector('[data-structure-variant]:not(.hidden) button[data-report-type]')
    if (card?.dataset.reportTypeLabel) rows.push({ label: "Format", value: card.dataset.reportTypeLabel })
    const basis = screen.querySelector('[data-nqr-review-field="portfolio_valuation_basis"]')
    const basisValue = basis && this.#fieldReviewValue(basis)
    if (basisValue) rows.push({ label: basis.dataset.nqrReviewLabel || "Basis of valuation", value: basisValue })
    return rows
  }

  #bulkUploadRows(screen) {
    return [
      ...this.#addressReviewRows(screen).map(row => ({ ...row, label: "Lead Address" })),
      ...this.#fieldReviewRows(screen),
      ...this.#totalCountRows(screen),
      ...this.#dataFileRows(screen),
    ]
  }

  #totalCountRows(screen) {
    const input = screen.querySelector('input[name="portfolio_total_property_count"]')
    const value = input?.value.trim()
    if (!value) return []
    const label = input.closest("[data-nqr-required-label]")?.dataset.nqrRequiredLabel
    return [{ label: label || "Total number of properties", value }]
  }

  // The spreadsheet only exists client-side until submit, so the downloadable link is a
  // local object URL over the file input's current selection. The anchor is built via the
  // DOM API because the file name is user data.
  #dataFileRows(screen) {
    const file = screen.querySelector('input[type="file"]')?.files?.[0]
    if (!file) return []
    if (this.bulkFileUrl) URL.revokeObjectURL(this.bulkFileUrl)
    this.bulkFileUrl = URL.createObjectURL(file)
    const link = document.createElement("a")
    link.href = this.bulkFileUrl
    link.download = file.name
    link.textContent = file.name
    link.className = "text-brand-1000 hover:opacity-70 underline break-all"
    return [{ label: "Data file", value: link }]
  }

  #addressReviewRows(screen) {
    const part = (target) => screen.querySelector(`[data-nqr-address-lookup-target="${target}"]`)?.value?.trim()
    const parts = ["line1", "line2", "line3", "line4", "city", "county", "postcode"].map(part).filter(Boolean)
    return parts.length ? [{ label: "Address", value: parts.join(", ") }] : []
  }

  #fieldReviewRows(screen) {
    return Array.from(screen.querySelectorAll('[id^="nqr-field-"]'))
      .filter((el) => !this.#hiddenWithin(el, screen))
      .map((el) => ({ label: this.#fieldReviewLabel(el), value: this.#fieldReviewValue(el) }))
      .filter(({ label, value }) => label && value)
  }

  #attachmentsReviewRows(screen) {
    const names = Array.from(screen.querySelectorAll('[data-controller~="file-upload"] .attachment [data-role="name"], [data-controller~="file-upload"] .attachment a'))
      .map((el) => el.textContent.trim())
      .filter(Boolean)
    return names.length ? [{ label: "Attachments", value: names.join(", ") }] : []
  }

  #contactsReviewRows(screen) {
    const selected = Array.from(screen.querySelectorAll('[data-controller~="multi-select-chips-with-search"] input[type="checkbox"]:checked'))
      .map((box) => (box.dataset.label || box.dataset.name || "").trim())
      .filter(Boolean)
    return selected.length ? [{ label: "Contacts", value: selected.join(", ") }] : []
  }

  #hiddenWithin(el, boundary) {
    for (let node = el; node && node !== boundary; node = node.parentElement) {
      if (node.classList.contains("hidden")) return true
    }
    return false
  }

  #fieldReviewLabel(el) {
    const fieldLabel = Array.from(el.querySelectorAll("[data-nqr-field-label]"))
      .find((label) => !this.#hiddenWithin(label, el))
    const text = fieldLabel?.textContent
      || el.dataset.nqrRequiredLabel
      || this.#humanizedFieldName(el.id.replace(/^nqr-field-/, ""))
    return text.trim().replace(/\s*\*\s*$/, "")
  }

  #humanizedFieldName(fieldName) {
    const name = fieldName.replace(/_/g, " ")
    return name.charAt(0).toUpperCase() + name.slice(1)
  }

  #fieldReviewValue(el) {
    const radio = el.querySelector('input[type="radio"]:checked')
    if (radio) return this.#checkedInputLabel(el, radio)

    if (el.querySelector("trix-editor")) {
      const html = el.querySelector('input[type="hidden"]')?.value
      return this.#trixTextOf(html) ? { html } : ""
    }

    const select = el.querySelector("select")
    if (select) return (select.selectedOptions[0]?.textContent || "").trim()

    const card = el.querySelector('button[data-report-type][data-selected="true"]')
    if (card) return card.dataset.reportTypeLabel || ""

    const buttonLabel = el.querySelector(
      '[data-single-select-field-target="buttonLabel"], [data-single-select-field-component-with-search-target="buttonLabel"]'
    )
    if (buttonLabel) {
      const hidden = el.querySelector('input[type="hidden"]')
      return hidden?.value.trim() ? buttonLabel.textContent.trim() : ""
    }

    return this.#inputReviewValue(el)
  }

  #checkedInputLabel(el, input) {
    const wrapping = input.closest("label")
    const text = wrapping?.querySelector('[data-radio-target="label"]')?.firstChild?.textContent
      || wrapping?.textContent
      || (input.id && el.querySelector(`label[for="${input.id}"]`)?.textContent)
      || input.dataset.label
      || input.value
    return (text || "").trim()
  }

  #inputReviewValue(el) {
    const visible = el.querySelector(
      'input:not([type="hidden"]):not([type="radio"]):not([type="checkbox"]):not([type="file"]), textarea'
    )
    const hidden = el.querySelector('input[type="hidden"]')
    const raw = (visible?.value || hidden?.value || "").trim()
    if (!raw) return ""
    const prefix = el.querySelector('[data-input-target="asset"]')?.textContent?.trim() || ""
    const value = prefix && /^\d+(\.\d+)?$/.test(raw) ? Number(raw).toLocaleString("en-GB") : raw
    return prefix + value
  }
}
